/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-study-crfs-grid.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect } from 'react';
import { orderBy } from '@progress/kendo-data-query';
import { CRFRowAction } from '../../central-monitoring/observations-grid-v2.interface';
import ExpandableGridV2 from '../../core/expandable/grid-v2';
import { ExtraMenuItem, ExtraMenuItemPosition } from '../../core/grid-header/grid-menu/grid-menu.interface';
import { ADMIN_STUDY_CRFS_COLUMNS } from './admin-study-crfs-grid.data';
import { AdminStudyCRFsGridInterface } from './admin-study-crfs-grid.interface';
import { useTranslation } from 'hooks';
import { StudyAssessmentFields } from 'queries/generated/fragments.generated';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import './styles.scss';
import { convertRules } from '@utils/convertor';

const AdminStudyCRFsGrid = ({
    loadedData,
    assessments,
    studyCrfs,
    setStudyCrfs,
    setCurrentCrf,
    showAddCrfDialog,
    canEdit,
    sysAdminCanEditStudy,
    assessmentAssociation,
}: AdminStudyCRFsGridInterface) => {
    const { t } = useTranslation();

    const actions: CRFRowAction = (assessment) => [
        {
            label: 'admin:editCrf',
            icon: ['fal', 'edit'],
            onClick: () => {
                setCurrentCrf(assessment);
                showAddCrfDialog();
            },
            enabled: canEdit || sysAdminCanEditStudy,
        },
    ];

    const getAssessmentName = (assessments: StudyAssessmentFields[], crfId: string) => {
        const assessmentsNames = assessments
            .filter((assessment) => assessment.crfId === crfId)
            .map((assessment) => assessment.name);
        return assessmentsNames.join(', ');
    };

    const restructuredCRFs = (items: any) => {
        if (items) {
            const newCRFs = items.map((item: any) => {
                return {
                    ...item,
                    _assessments: assessmentAssociation ? getAssessmentName(assessments, item.id) : '',
                    _crfRules: convertRules(item.crfRules),
                };
            });

            const newData = restructuredUpdatedCreatedBy(newCRFs);
            setStudyCrfs(orderBy(newData, [{ field: 'crfName', dir: 'asc' }]) ?? []);
        }
    };

    useEffect(() => {
        restructuredCRFs(loadedData);
    }, [loadedData, assessments]);

    const extraMenuItems: ExtraMenuItem[] = [
        {
            position: ExtraMenuItemPosition.right,
            tooltipTitle: t('admin:addCrf'),
            onClick: showAddCrfDialog,
            customClassName: 'grid-header__item--always-visible',
            icon: ['fal', 'plus'],
            disabled: !canEdit,
        },
    ];

    return (
        <ExpandableGridV2
            title={t('admin:manageCRFs')}
            hideCheckbox
            hideExpand
            customKey="admin-study-crfs-grid"
            id="crfs-grid"
            key="crfs-grid"
            items={studyCrfs}
            rowActions={actions}
            scrollable="scrollable"
            groupable
            reorderable
            resizable
            showPager
            pageable={false}
            extraMenuItems={extraMenuItems}
            columns={ADMIN_STUDY_CRFS_COLUMNS(t)}
        />
    );
};

export default AdminStudyCRFsGrid;
